interface Params<T extends object> {
  get: () => T[];
  set: (value: T[]) => void;
  shouldCollapse: (a: T, b: T) => boolean;

  onUpdate?: (p: { current: T; previous?: T; collapsed: boolean }) => void;
}

// this is meant to be paired with Zustand and allows you to create a simple history stack
// with specific rules for collapsing items
export const createCollapsibleHistoryStack = <T extends object>(params: Params<T>) => {
  return {
    update(item: T) {
      const items = params.get();
      const updatedItems = [...items];

      const previousCurrentItem = items[items.length - 1];

      let collapsed = false;

      if (items.length > 0 && params.shouldCollapse(previousCurrentItem, item)) {
        updatedItems[items.length - 1] = item;
        collapsed = true;
      } else {
        updatedItems.push(item);
      }

      params.set(updatedItems);

      params.onUpdate?.({ current: item, previous: previousCurrentItem, collapsed });
    },
    current(): T | undefined {
      return params.get()[params.get().length - 1];
    },
    reset(item?: T) {
      params.set(item ? [item] : []);
    },
    findMostRecent(predicate: (item: T) => boolean) {
      const items = params.get();
      return items.findLast((item) => predicate(item));
    },
    popUntil(predicate: (item: T) => boolean) {
      const items = params.get();
      const previousCurrentItem = items[items.length - 1];
      const index = items.findLastIndex((item) => predicate(item));

      if (index === -1) {
        return;
      }

      params.set(items.slice(0, index + 1));

      const currentItem = items[index];

      params.onUpdate?.({ current: currentItem, previous: previousCurrentItem, collapsed: false });

      return currentItem;
    },
  };
};
